import { useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, IconButton, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Close, ContentCopy, DeleteOutline, Search } from '@mui/icons-material';
import type {
  IntakeFormDef, IntakeFormTest, PublicIntakeDraft, PublicIntakeSample,
} from '../../../../types/intake';
import {
  EMPTY_SAMPLE, testQuantity, withTestQuantity, withStresses, withConfiningPressure, deriveActiveHints,
  type SampleField,
} from '../intakeFormState';
import { AdvisoryHints } from '../components/AdvisoryHints';
import { StressesEditor } from './StressesEditor';
import { ConfiningPressureField } from './ConfiningPressureField';
import { fieldFlashSx } from './ProjectInfoStep';

interface Props {
  def: IntakeFormDef;
  draft: PublicIntakeDraft;
  onChange: (samples: PublicIntakeSample[]) => void;
  /** Per-row fields that failed validation — highlighted red and flashed. */
  errorRows?: Record<number, SampleField[]>;
  /** Bump to re-trigger the flash animation on the same cells. */
  flashNonce?: number;
  /**
   * Existing Location IDs of the ATTACHED project (signed-in users only) —
   * offered as suggestions so repeat submissions land in the same locations
   * instead of near-duplicate spellings. Free typing stays allowed.
   */
  locationOptions?: string[];
}

/** Frozen sample-detail columns (the workbook's frozen pane). */
const STICKY_COLS: { key: string; label: string; width: number; hint?: string }[] = [
  { key: 'idx', label: '#', width: 36 },
  { key: 'locationId', label: 'Location ID', width: 140, hint: 'Borehole or test pit name, e.g. BH-BGC21-01, TP-BGC22-04' },
  { key: 'topDepth', label: 'Top (m)', width: 82, hint: 'Required — metres, two decimal places' },
  { key: 'baseDepth', label: 'Base (m)', width: 82, hint: 'Optional — must be below the top depth' },
  { key: 'sampleRef', label: 'Sample Ref', width: 116, hint: 'Sample reference in OpenGround' },
  { key: 'sampleType', label: 'Sample Type', width: 156, hint: 'The "Description" — consistent with OpenGround' },
  { key: 'actions', label: '', width: 76 },
];
const stickyLeft = (i: number) => STICKY_COLS.slice(0, i).reduce((acc, c) => acc + c.width, 0);
const TOTAL_STICKY = STICKY_COLS.reduce((acc, c) => acc + c.width, 0);
const TEST_COL_WIDTH = 120;

const headerCellSx = {
  bgcolor: 'grey.100',
  fontWeight: 600,
  fontSize: 12,
  lineHeight: 1.25,
  py: 0.75,
} as const;

/** Quantity input without the browser's up/down spinners. */
const qtyInputSx = {
  width: 52,
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none', margin: 0,
  },
  '& input[type=number]': { MozAppearance: 'textfield' },
} as const;

/**
 * Step 2 — samples × tests. Instead of showing all ~30 catalog columns, the
 * client SEARCHES for the tests they need (by name or ASTM code, grouped by
 * category); each picked test becomes a column with quantity cells per
 * sample. Removing a column clears its quantities.
 */
export const SamplesGridStep = ({ def, draft, onChange, errorRows = {}, flashNonce = 0, locationOptions = [] }: Props) => {
  const samples = draft.samples ?? [];
  const rowErr = (i: number, f: SampleField) => (errorRows[i] ?? []).includes(f);
  /** error + flash props for a sample-detail cell input. */
  const errProps = (i: number, f: SampleField) => {
    const hasError = rowErr(i, f);
    return {
      key: hasError ? `${i}-${f}-${flashNonce}` : `${i}-${f}`,
      error: hasError,
      sx: hasError ? fieldFlashSx : undefined,
    };
  };
  const allTests: IntakeFormTest[] = useMemo(
    () => def.categories.flatMap((c) => c.tests), [def],
  );

  // Columns = tests referenced by any sample + tests explicitly added.
  const [addedKeys, setAddedKeys] = useState<string[]>([]);
  const columns = useMemo(() => {
    const used = new Set<string>(addedKeys.map((k) => k.toUpperCase()));
    for (const s of samples) for (const t of s.tests) used.add(t.testKey.toUpperCase());
    return allTests.filter((t) => used.has(t.key.toUpperCase()));
  }, [allTests, samples, addedKeys]);
  const columnKeySet = useMemo(
    () => new Set(columns.map((c) => c.key.toUpperCase())), [columns],
  );
  // BUG-2026-0182: columns follow category order (rock first), so each
  // category is one block — label the first column of each block and draw a
  // heavy rule at every boundary (the rock | soil "line").
  const groupStartKeys = useMemo(() => {
    const starts = new Set<string>();
    columns.forEach((t, i) => {
      if (i === 0 || columns[i - 1].category !== t.category) starts.add(t.key.toUpperCase());
    });
    return starts;
  }, [columns]);
  const groupRuleSx = (t: IntakeFormTest, index: number) =>
    (index > 0 && groupStartKeys.has(t.key.toUpperCase())
      ? { borderLeft: 3, borderColor: 'primary.main' }
      : {});

  const setSample = (i: number, next: PublicIntakeSample) => {
    onChange(samples.map((s, j) => (j === i ? next : s)));
  };
  const numeric = (v: string): number | null => (v === '' ? null : Number(v));

  const addTestColumn = (test: IntakeFormTest | null) => {
    if (!test || columnKeySet.has(test.key.toUpperCase())) return;
    setAddedKeys((prev) => [...prev, test.key]);
    // One sample row? Pre-fill quantity 1 so the common case is one click.
    if (samples.length === 1 && testQuantity(samples[0], test.key) === 0) {
      setSample(0, withTestQuantity(samples[0], test.key, 1));
    }
  };

  const removeTestColumn = (test: IntakeFormTest) => {
    setAddedKeys((prev) => prev.filter((k) => k.toUpperCase() !== test.key.toUpperCase()));
    onChange(samples.map((s) => withTestQuantity(s, test.key, 0)));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '58vh' }}>
      <AdvisoryHints hints={deriveActiveHints(draft, def)} />

      {/* Toolbar: test search + add sample */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Autocomplete
          options={allTests.filter((t) => !columnKeySet.has(t.key.toUpperCase()))}
          groupBy={(t) => t.category}
          getOptionLabel={(t) => (t.astm ? `${t.name} (${t.astm})` : t.name)}
          onChange={(_, value) => addTestColumn(value)}
          value={null}
          blurOnSelect
          clearOnBlur
          renderInput={(params) => (
            <TextField
              {...params} size="small" placeholder="Search tests by name or ASTM code…"
              InputProps={{
                ...params.InputProps,
                startAdornment: <Search sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />,
              }}
            />
          )}
          sx={{ width: 380 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
          Pick the tests you need — each becomes a column. Enter a quantity per sample
          (1 = one test; 3 = a three-point envelope).
        </Typography>
        <Button
          size="small" variant="outlined" startIcon={<Add />}
          onClick={() => onChange([...samples, { ...EMPTY_SAMPLE }])}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          Add sample
        </Button>
      </Box>

      {/* Selected-test chips (quick remove + overview) */}
      {columns.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {columns.map((t) => (
            <Chip
              key={t.key}
              size="small"
              label={t.astm ? `${t.name} (${t.astm})` : t.name}
              onDelete={() => removeTestColumn(t)}
              sx={{ fontSize: '0.7rem' }}
            />
          ))}
        </Box>
      )}

      <TableContainer sx={{ flex: 1, minHeight: '38vh', maxHeight: '58vh', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader sx={{ minWidth: TOTAL_STICKY + Math.max(columns.length, 3) * TEST_COL_WIDTH }}>
          <TableHead>
            <TableRow>
              {STICKY_COLS.map((col, i) => (
                <TableCell
                  key={col.key}
                  sx={{
                    ...headerCellSx, position: 'sticky', left: stickyLeft(i), zIndex: 4,
                    minWidth: col.width, maxWidth: col.width,
                  }}
                >
                  {col.hint ? <Tooltip title={col.hint}><span>{col.label}</span></Tooltip> : col.label}
                </TableCell>
              ))}
              {columns.map((t, ci) => (
                <TableCell
                  key={t.key}
                  align="center"
                  sx={{
                    ...headerCellSx, minWidth: TEST_COL_WIDTH, maxWidth: TEST_COL_WIDTH, verticalAlign: 'bottom',
                    ...groupRuleSx(t, ci),
                  }}
                >
                  {groupStartKeys.has(t.key.toUpperCase()) && (
                    <Typography
                      sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'primary.main', mb: 0.25 }}
                    >
                      {t.category}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 0.25 }}>
                    <Tooltip title={t.astm ? `${t.name} (${t.astm}) — ${t.category}` : t.name}>
                      <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>{t.name}</Typography>
                        {t.astm && <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{t.astm}</Typography>}
                      </Box>
                    </Tooltip>
                    <Tooltip title="Remove this test">
                      <IconButton size="small" onClick={() => removeTestColumn(t)} sx={{ p: 0.25, mt: -0.25 }}>
                        <Close sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
              {columns.length === 0 && (
                <TableCell sx={{ ...headerCellSx, minWidth: 3 * TEST_COL_WIDTH }} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {samples.map((s, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ position: 'sticky', left: stickyLeft(0), bgcolor: 'background.paper', zIndex: 2 }}>
                  {i + 1}
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: stickyLeft(1), bgcolor: 'background.paper', zIndex: 2, p: 0.5 }}>
                  {locationOptions.length > 0 ? (
                    <Autocomplete
                      freeSolo disableClearable
                      options={locationOptions}
                      value={s.locationId ?? ''}
                      onChange={(_, v) => setSample(i, { ...s, locationId: (v as string) || null })}
                      onInputChange={(_, text, reason) => {
                        if (reason === 'input') setSample(i, { ...s, locationId: text || null });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          {...errProps(i, 'locationId')}
                          size="small" variant="standard" placeholder="BH-BGC21-01" fullWidth
                          inputProps={{ ...params.inputProps, style: { fontSize: 13 }, autoComplete: 'off' }}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      {...errProps(i, 'locationId')}
                      size="small" variant="standard" placeholder="BH-BGC21-01" fullWidth
                      value={s.locationId ?? ''}
                      onChange={(e) => setSample(i, { ...s, locationId: e.target.value || null })}
                      inputProps={{ style: { fontSize: 13 }, autoComplete: 'off' }}
                    />
                  )}
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: stickyLeft(2), bgcolor: 'background.paper', zIndex: 2, p: 0.5 }}>
                  <TextField
                    {...errProps(i, 'topDepth')}
                    size="small" variant="standard" type="number" fullWidth
                    value={s.topDepth ?? ''}
                    onChange={(e) => setSample(i, { ...s, topDepth: numeric(e.target.value) })}
                    inputProps={{ min: 0, step: 0.01, style: { fontSize: 13 }, autoComplete: 'off' }}
                    sx={rowErr(i, 'topDepth') ? { ...qtyInputSx, ...fieldFlashSx } : qtyInputSx}
                  />
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: stickyLeft(3), bgcolor: 'background.paper', zIndex: 2, p: 0.5 }}>
                  <TextField
                    {...errProps(i, 'baseDepth')}
                    size="small" variant="standard" type="number" fullWidth
                    value={s.baseDepth ?? ''}
                    onChange={(e) => setSample(i, { ...s, baseDepth: numeric(e.target.value) })}
                    inputProps={{ min: 0, step: 0.01, style: { fontSize: 13 }, autoComplete: 'off' }}
                    sx={rowErr(i, 'baseDepth') ? { ...qtyInputSx, ...fieldFlashSx } : qtyInputSx}
                  />
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: stickyLeft(4), bgcolor: 'background.paper', zIndex: 2, p: 0.5 }}>
                  <TextField
                    size="small" variant="standard" fullWidth
                    value={s.sampleRef ?? ''}
                    onChange={(e) => setSample(i, { ...s, sampleRef: e.target.value || null })}
                    inputProps={{ style: { fontSize: 13 }, autoComplete: 'off' }}
                  />
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: stickyLeft(5), bgcolor: 'background.paper', zIndex: 2, p: 0.5 }}>
                  <TextField
                    size="small" variant="standard" select fullWidth
                    value={s.sampleType ?? ''}
                    onChange={(e) => setSample(i, { ...s, sampleType: e.target.value || null })}
                    SelectProps={{ displayEmpty: true }}
                    inputProps={{ style: { fontSize: 13 }, autoComplete: 'off' }}
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {def.sampleTypeOptions.map((o) => (
                      <MenuItem key={o.code} value={o.code}>{o.description}</MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: stickyLeft(6), bgcolor: 'background.paper', zIndex: 2, p: 0, whiteSpace: 'nowrap' }}>
                  <Tooltip title="Duplicate row">
                    <IconButton size="small" onClick={() => onChange([
                      ...samples.slice(0, i + 1),
                      { ...s, tests: s.tests.map((t) => ({ ...t, params: t.params ? { ...t.params } : null })) },
                      ...samples.slice(i + 1),
                    ])}>
                      <ContentCopy sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove row">
                    <IconButton size="small" onClick={() => onChange(samples.filter((_, j) => j !== i))}>
                      <DeleteOutline sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                {columns.map((t, ci) => {
                  const qty = testQuantity(s, t.key);
                  const rowTest = s.tests.find((x) => x.testKey.toUpperCase() === t.key.toUpperCase());
                  const stresses = rowTest?.params?.stressesKpa ?? [];
                  const confining = rowTest?.params?.confiningPressureMPa ?? null;
                  const testsFlagged = rowErr(i, 'tests') && qty > 0;
                  return (
                    <TableCell key={t.key} align="center" sx={{ p: 0.5, ...groupRuleSx(t, ci) }}>
                      <TextField
                        key={testsFlagged ? `q-${i}-${t.key}-${flashNonce}` : `q-${i}-${t.key}`}
                        size="small" variant="standard" type="number"
                        value={qty || ''}
                        placeholder="—"
                        error={testsFlagged}
                        onChange={(e) => {
                          const v = e.target.value === '' ? 0 : Math.max(0, Math.floor(Number(e.target.value)));
                          setSample(i, withTestQuantity(s, t.key, v));
                        }}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, step: 1, style: { fontSize: 13, textAlign: 'center' }, autoComplete: 'off' }}
                        sx={testsFlagged ? { ...qtyInputSx, ...fieldFlashSx } : qtyInputSx}
                      />
                      {t.paramKind === 'stressesKpa' && qty > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <StressesEditor
                            stresses={stresses}
                            onChange={(next) => setSample(i, withStresses(s, t.key, next))}
                          />
                        </Box>
                      )}
                      {t.paramKind === 'confiningPressureMPa' && qty > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <ConfiningPressureField
                            valueMPa={confining}
                            onChange={(next) => setSample(i, withConfiningPressure(s, t.key, next))}
                          />
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
                {columns.length === 0 && i === 0 && (
                  <TableCell rowSpan={Math.max(samples.length, 1)} sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ py: 3 }}>
                      No tests picked yet — use the search box above to add the tests you need.
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {samples.length === 0 && (
              <TableRow>
                <TableCell colSpan={STICKY_COLS.length + Math.max(columns.length, 1)}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    No samples yet — click "Add sample" to start.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
